import { gql } from '@apollo/client'

// Authentication Mutations
export const LOGIN = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      user {
        id
        email
        name
        active
      }
      role {
        id
        role
        description
        permissions {
          id
          name
          description
        }
      }
      token
      refreshToken
      expiresIn
      availableRoles {
        id
        role
        description
      }
      requiresRoleSelection
    }
  }
`

export const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      user {
        id
        email
        name
        active
      }
      role {
        id
        role
        description
        permissions {
          id
          name
          description
        }
      }
      token
      expiresIn
    }
  }
`

export const REFRESH_TOKEN = gql`
  mutation RefreshToken($input: RefreshTokenInput!) {
    refreshToken(input: $input) {
      token
      refreshToken
    }
  }
`

// User Mutations
export const UPDATE_USER = gql`
  mutation UpdateUser($id: Int!, $updateData: UpdateUserData!) {
    updateUser(id: $id, updateData: $updateData) {
      id
      email
      name
      phone_number
      active
      invite_pending
      updated_at
      roles {
        id
        role
        description
      }
    }
  }
`

export const INVITE_USER = gql`
  mutation InviteUser($email: String!, $role_ids: [Int!]!) {
    inviteUser(email: $email, role_ids: $role_ids) {
      id
      email
      name
      active
      invite_pending
      roles {
        id
        role
        description
      }
    }
  }
`

export const RESEND_INVITE = gql`
  mutation ResendInvite($userId: Int!) {
    resendInvite(userId: $userId)
  }
`

export const ACCEPT_INVITE = gql`
  mutation AcceptInvite($token: String!, $name: String!, $password: String!) {
    acceptInvite(token: $token, name: $name, password: $password)
  }
`

export const REQUEST_PASSWORD_RESET = gql`
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(email: $email)
  }
`

export const RESET_PASSWORD = gql`
  mutation ResetPassword($token: String!, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword)
  }
`

export const UPDATE_MY_PROFILE = gql`
  mutation UpdateMyProfile($name: String, $phone_number: String) {
    updateMyProfile(name: $name, phone_number: $phone_number) {
      id
      email
      name
      phone_number
    }
  }
`

export const CHANGE_MY_PASSWORD = gql`
  mutation ChangeMyPassword($currentPassword: String!, $newPassword: String!) {
    changeMyPassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`

export const DELETE_USER = gql`
  mutation DeleteUser($id: Int!) {
    deleteUser(id: $id)
  }
`

export const ACTIVATE_USER = gql`
  mutation ActivateUser($id: Int!) {
    activateUser(id: $id) {
      id
      email
      name
      active
    }
  }
`

export const DEACTIVATE_USER = gql`
  mutation DeactivateUser($id: Int!) {
    deactivateUser(id: $id) {
      id
      email
      name
      active
    }
  }
`

export const VERIFY_PASSWORD = gql`
  mutation VerifyPassword($userId: Int!, $password: String!) {
    verifyPassword(userId: $userId, password: $password) {
      isValid
    }
  }
`

// Role Mutations
export const CREATE_ROLE = gql`
  mutation CreateRole($roleData: CreateRoleData!) {
    createRole(roleData: $roleData) {
      id
      role
      description
      active
      created_at
    }
  }
`

export const UPDATE_ROLE = gql`
  mutation UpdateRole($id: Int!, $roleData: UpdateRoleData!) {
    updateRole(id: $id, roleData: $roleData) {
      id
      role
      description
      active
      updated_at
    }
  }
`

export const DELETE_ROLE = gql`
  mutation DeleteRole($id: Int!) {
    deleteRole(id: $id)
  }
`

export const ACTIVATE_ROLE = gql`
  mutation ActivateRole($id: Int!) {
    activateRole(id: $id) {
      id
      role
      active
    }
  }
`

export const DEACTIVATE_ROLE = gql`
  mutation DeactivateRole($id: Int!) {
    deactivateRole(id: $id) {
      id
      role
      active
    }
  }
`

export const ASSOCIATE_ROLE_WITH_PERMISSIONS = gql`
  mutation AssociateRoleWithPermissions($roleId: Int!, $permissionIds: [Int!]!) {
    associateRoleWithPermissions(roleId: $roleId, permissionIds: $permissionIds)
  }
`

export const REMOVE_ROLE_PERMISSION_ASSOCIATIONS = gql`
  mutation RemoveRolePermissionAssociations($roleId: Int!, $permissionIds: [Int!]!) {
    removeRolePermissionAssociations(roleId: $roleId, permissionIds: $permissionIds)
  }
`

// Option Group Mutations
export const CREATE_OPTION_GROUP = gql`
  mutation CreateOptionGroup($data: CreateOptionGroupData!) {
    createOptionGroup(data: $data) {
      id
      name
      description
      min_select
      max_select
      ask_during_add
      active
      sort_order
    }
  }
`

export const UPDATE_OPTION_GROUP = gql`
  mutation UpdateOptionGroup($id: Int!, $data: UpdateOptionGroupData!) {
    updateOptionGroup(id: $id, data: $data) {
      id
      name
      description
      min_select
      max_select
      ask_during_add
      active
      sort_order
    }
  }
`

export const DELETE_OPTION_GROUP = gql`
  mutation DeleteOptionGroup($id: Int!) {
    deleteOptionGroup(id: $id)
  }
`

// Option Item Mutations
export const CREATE_OPTION_ITEM = gql`
  mutation CreateOptionItem($data: CreateOptionItemData!) {
    createOptionItem(data: $data) {
      id
      option_group_id
      label
      product_id
      price_delta_cents
      default_selected
      sort_order
      effect_json
      image_url
    }
  }
`

export const UPDATE_OPTION_ITEM = gql`
  mutation UpdateOptionItem($id: Int!, $data: UpdateOptionItemData!) {
    updateOptionItem(id: $id, data: $data) {
      id
      option_group_id
      label
      product_id
      price_delta_cents
      default_selected
      sort_order
      effect_json
      planning_relevant
      image_url
    }
  }
`

export const DELETE_OPTION_ITEM = gql`
  mutation DeleteOptionItem($id: Int!) {
    deleteOptionItem(id: $id)
  }
`

// Variant Option Group Mutations
export const CREATE_VARIANT_OPTION_GROUP = gql`
  mutation CreateVariantOptionGroup($data: CreateVariantOptionGroupData!) {
    createVariantOptionGroup(data: $data) {
      id
      product_variant_id
      option_group_id
      sort_order
    }
  }
`

export const UPDATE_VARIANT_OPTION_GROUP = gql`
  mutation UpdateVariantOptionGroup($id: Int!, $data: UpdateVariantOptionGroupData!) {
    updateVariantOptionGroup(id: $id, data: $data) {
      id
      product_variant_id
      option_group_id
      sort_order
    }
  }
`

export const DELETE_VARIANT_OPTION_GROUP = gql`
  mutation DeleteVariantOptionGroup($id: Int!) {
    deleteVariantOptionGroup(id: $id)
  }
`

export const DELETE_VARIANT_OPTION_GROUP_BY_VARIANT_AND_GROUP = gql`
  mutation DeleteVariantOptionGroupByVariantAndGroup($productVariantId: Int!, $optionGroupId: Int!) {
    deleteVariantOptionGroupByVariantAndGroup(productVariantId: $productVariantId, optionGroupId: $optionGroupId)
  }
`

// Bundle Group Mutations
export const UPDATE_BUNDLE_GROUP = gql`
  mutation UpdateBundleGroup($id: Int!, $bundleGroupData: CreateBundleGroupData!) {
    updateBundleGroup(id: $id, bundleGroupData: $bundleGroupData) {
      id
      name
      required_count
      sort_order
    }
  }
`

export const CREATE_BUNDLE_GROUP_OPTION = gql`
  mutation CreateBundleGroupOption($optionData: CreateBundleGroupOptionData!) {
    createBundleGroupOption(optionData: $optionData) {
      id
      bundle_group_id
      eligible_variant_id
      price_delta_cents
      sort_order
    }
  }
`

export const UPDATE_BUNDLE_GROUP_OPTION = gql`
  mutation UpdateBundleGroupOption($id: Int!, $optionData: UpdateBundleGroupOptionData!) {
    updateBundleGroupOption(id: $id, optionData: $optionData) {
      id
      price_delta_cents
      sort_order
    }
  }
`

export const DELETE_BUNDLE_GROUP_OPTION = gql`
  mutation DeleteBundleGroupOption($id: Int!) {
    deleteBundleGroupOption(id: $id)
  }
`

// Order Mutations
export const CREATE_ORDER = gql`
  mutation CreateOrder($orderData: CreateOrderData!) {
    createOrder(orderData: $orderData) {
      id
      order_number
      status
      order_type
      subtotal_cents
      total_cents
      customer_name
      notes
      created_at
      staffUser {
        id
        name
        email
      }
      orderLines {
        id
        product_variant_id
        quantity
        unit_price_cents
        subtotal_cents
        tax_rate_percentage
        tax_amount_cents
        total_cents
        customizations {
          label
          group_name
          price_delta_cents
        }
        customizations_json
        notes
        parent_order_line_id
        relationship_type
        relationship_group_id
        sort_order
        productVariant {
          id
          name
          price_cents
          product {
            id
            name
            kind
          }
        }
        parentOrderLine {
          id
          productVariant {
            name
          }
        }
        childOrderLines {
          id
          productVariant {
            name
          }
          relationship_type
          customizations {
            label
            group_name
            price_delta_cents
          }
          customizations_json
        }
      }
      payment {
        id
        payment_method
        status
        amount_cents
        transaction_id
        processed_at
      }
      consumingEmployee {
        id
        name
      }
    }
  }
`

// Pay an EXISTING unpaid POS order again after a declined / cancelled card (API 2026-10-08,
// docs/frontend/payment-integration-guide.md → "Paying an existing order again"). Same
// payment input as createOrder; the amount is the server's (the open balance). The order
// is already in the normalized cache from createOrder, so only what the checkout's
// completed view and payment handling read is selected — the new `payment` above all.
export const RETRY_ORDER_PAYMENT = gql`
  mutation RetryOrderPayment($orderId: Int!, $payment: CreateOrderPaymentData!) {
    retryOrderPayment(order_id: $orderId, payment: $payment) {
      id
      order_number
      status
      order_type
      total_cents
      customer_name
      created_at
      updated_at
      payment {
        id
        payment_method
        status
        amount_cents
        transaction_id
        processed_at
      }
    }
  }
`

export const UPDATE_ORDER_STATUS = gql`
  mutation UpdateOrderStatus($id: Int!, $status: OrderStatus!) {
    updateOrderStatus(id: $id, status: $status) {
      id
      order_number
      status
      current_status_since
      customer_name
      order_type
    }
  }
`

// Operator marks an incoming HubRise order as handled outside the POS.
// Order flips received → completed with externally_handled=true; HubRise is
// PATCHed `accepted`. unresolved_lines_json is preserved as a forensic record.
export const COMPLETE_INCOMING_ORDER_EXTERNALLY = gql`
  mutation CompleteIncomingOrderExternally($orderId: Int!, $reason: String) {
    completeIncomingOrderExternally(orderId: $orderId, reason: $reason) {
      id
      status
      externally_handled
    }
  }
`

// Payment Mutations

export const CANCEL_PAYMENT = gql`
  mutation CancelPayment($terminal_id: String!) {
    cancelPayment(terminal_id: $terminal_id) {
      success
      message
      reference
      payment_id
    }
  }
`

export const CANCEL_CARD_PAYMENT = gql`
  mutation CancelCardPayment($payment_id: Int!) {
    cancelCardPaymentById(payment_id: $payment_id) {
      success
      message
      reference
      payment_id
    }
  }
`

// Refund Mutations
export const REFUND_ORDER_FULL = gql`
  mutation RefundOrderFull(
    $order_id: Int!
    $payment_config: PaymentConfigData
  ) {
    refundOrderFull(
      order_id: $order_id
      payment_config: $payment_config
    ) {
      success
      message
      payment {
        id
        order_id
        amount_cents
        payment_method
        status
        transaction_type
        transaction_id
        processed_at
        # Action flags - check these when status === 'COMPLETED'
        ask_merchant_signature
        ask_customer_signature
        ask_customer_identification
        print_customer_receipt
      }
      order {
        id
        order_number
        status
        total_cents
      }
    }
  }
`

export const REFUND_ORDER_PARTIAL = gql`
  mutation RefundOrderPartial(
    $order_id: Int!
    $amount_cents: Int!
    $payment_config: PaymentConfigData
  ) {
    refundOrderPartial(
      order_id: $order_id
      amount_cents: $amount_cents
      payment_config: $payment_config
    ) {
      success
      message
      payment {
        id
        order_id
        amount_cents
        payment_method
        status
        transaction_type
        transaction_id
        processed_at
        # Action flags - check these when status === 'COMPLETED'
        ask_merchant_signature
        ask_customer_signature
        ask_customer_identification
        print_customer_receipt
      }
      order {
        id
        order_number
        status
        total_cents
      }
    }
  }
`

// Record a refund made OUTSIDE our system — Mollie dashboard for a webshop order, Uber Eats /
// Thuisbezorgd for a platform order — so the books match. Nothing is triggered at Mollie or the
// platform; the API writes a completed REFUND / PARTIAL_REFUND row at once (no terminal polling).
// Permission refund.record_external. Contract: API docs/frontend/payment-integration-guide.md
// § "Recording a refund made outside our system (2026-10-09)".
export const RECORD_EXTERNAL_REFUND = gql`
  mutation RecordExternalRefund($order_id: Int!, $amount_cents: Int!) {
    recordExternalRefund(order_id: $order_id, amount_cents: $amount_cents) {
      success
      message
      payment {
        id
        order_id
        amount_cents
        payment_method
        status
        transaction_type
        transaction_id
        provider
        processed_at
        created_at
      }
      order {
        id
        status
      }
    }
  }
`

// Receipt Queries
export const GET_RECEIPT = gql`
  query GetReceipt($order_id: Int!, $receipt_type: ReceiptType!) {
    receipt(order_id: $order_id, receipt_type: $receipt_type) {
      receipt_id
      receipt_url
      order_number
      order_date
      payment_date
      total_amount
      payment_method
      transaction_id
      terminal_id
      staff_name
      customer_name
      items {
        name
        quantity
        unit_price
        total_price
      }
      subtotal
      # NET of VAT and NULL on non-delivery orders — see the Receipt type in
      # types/order.ts. Selected so the on-screen totals close for first-party
      # delivery orders; the printed receipt renders receipt_full_text, which has
      # always carried the Bezorgkosten line.
      statiegeld
      verpakkingskosten
      delivery_fee
      tax_amount
      total
    }
  }
`

export const GET_RECEIPT_BY_PAYMENT = gql`
  query GetReceiptByPayment($payment_id: Int!, $receipt_type: ReceiptType!) {
    receiptByPayment(payment_id: $payment_id, receipt_type: $receipt_type) {
      receipt_id
      receipt_url
      order_number
      order_date
      payment_date
      total_amount
      payment_method
      transaction_id
      terminal_id
      items {
        name
        quantity
        unit_price
        total_price
      }
      subtotal
      statiegeld
      verpakkingskosten
      delivery_fee
      tax_amount
      total
    }
  }
`


